import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import { parseAmount } from "../../constants/validationFunctions";
import { countryToIso } from "../../sanitize/foreignAddress";
import { streetLinesWithUnit } from "./streetLines";
import { Record1098 } from "./interface/1098-interface";

/**
 * Form 1098 (Mortgage Interest Statement) detail shared by the original and
 * correction generators. Element order follows Form1098DetailType in
 * schemas/TY2026/FORM_TYPES/IRS-Form1098Type.xsd.
 *
 * Ported from core-api efile/A2A-iris/1098/detail.ts (TS-8587). The only change is that the
 * IRS country table is read from the context's schema root, the way every engine caller of
 * countryToIso does; the mapping itself is unchanged.
 *
 * The rows come straight from form1098_{year}, whose columns carry the client
 * template's names (BORROWER_*, LENDER_*). The shared recipient builder reads
 * RECIPIENT_* keys, so the borrower is translated once, here.
 */

const text = (v: unknown): string => String(v ?? "").trim();

/** Same shape the shared split-name builder expects from the 1099 forms. */
export function borrowerAsRecipient(record: Record1098): Record<string, any> {
  return {
    RECIPIENT_ID_TYPE: text(record.BORROWER_ID_TYPE),
    RECIPIENT_FIRST_NAME: text(record.BORROWER_FIRST_NAME),
    RECIPIENT_MIDDLE_NAME: text(record.BORROWER_MIDDLE_NAME),
    RECIPIENT_LAST_NAME: text(record.BORROWER_LAST_NAME),
    RECIPIENT_SUFFIX: text(record.BORROWER_SUFFIX),
    RECIPIENT_BUSINESS_NAME1: text(record.BORROWER_COMPANY_NAME),
    RECIPIENT_BUSINESS_NAME2: text(record.RECIPIENT_BUSINESS_NAME2),
  };
}

const isChecked = (v: unknown): boolean =>
  v === true || ["X", "1", "TRUE", "Y"].includes(text(v).toUpperCase());

export const isForeignBorrower = (record: Record1098): boolean =>
  isChecked(record.FOREIGN_COUNTRY_INDICATOR) || text(record.BORROWER_COUNTRY) !== "";

export function borrowerTin(record: Record1098): string {
  return text(record.BORROWER_ID_NUMBER).replace(/\D/g, "") || "000000000";
}

/** Borrower mailing address: USAddress, or ForeignAddress when the row is foreign. */
export function borrowerMailingAddress(record: Record1098, schemaRoot?: string): object {
  const { line1, line2 } = streetLinesWithUnit(
    record.BORROWER_ADDRESS1,
    record.BORROWER_ADDRESS2,
    record.BORROWER_APT_NO,
  );
  if (isForeignBorrower(record)) {
    const country = text(record.BORROWER_COUNTRY);
    return {
      ForeignAddress: {
        AddressLine1Txt: line1 || "NA",
        ...(line2 && { AddressLine2Txt: line2 }),
        ...(text(record.BORROWER_CITY) && { CityNm: text(record.BORROWER_CITY) }),
        ...(text(record.BORROWER_STATE) && { ProvinceOrStateNm: text(record.BORROWER_STATE) }),
        // An unresolvable name goes through raw so the XSD names CountryCd.
        CountryCd: countryToIso(country, schemaRoot) ?? country.replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase(),
        ...(text(record.BORROWER_ZIP) && { ForeignPostalCd: text(record.BORROWER_ZIP) }),
      },
    };
  }
  return {
    USAddress: {
      AddressLine1Txt: line1 || "UNKNOWN ADDRESS",
      ...(line2 && { AddressLine2Txt: line2 }),
      CityNm: text(record.BORROWER_CITY) || "UNKNOWN",
      StateAbbreviationCd: text(record.BORROWER_STATE).toUpperCase().slice(0, 2),
      ZIPCd: text(record.BORROWER_ZIP) || "00000",
    },
  };
}

/**
 * "MM/DD/YYYY" or "YYYY-MM-DD" (with or without a time) to the IRS
 * DateNoTimeZoneType. Parsed by hand: new Date("01/13/2023") is local time and
 * shifts a day when serialised as UTC east of Greenwich.
 */
export function irsDate(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  const v = text(value);
  if (!v) return "";
  let m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  return "";
}

const hasValue = (v: unknown): boolean => text(v) !== "";

/**
 * Box 10 ("Other") lines, in print order: the client's own BOX10_OTHER text,
 * then one "LABEL $ amount" line for each of AMORTIZATION, REAL_ESTATE_TAXES and
 * CAPITAL_RESERVE that carries a value (blank is skipped, zero is printed).
 * tabservice-forms composes the printed box the same way.
 */
export const BOX10_AMOUNT_LINES: ReadonlyArray<[keyof Record1098, string]> = [
  ["AMORTIZATION", "AMORTIZATION"],
  ["REAL_ESTATE_TAXES", "R/E TAXES"],
  ["CAPITAL_RESERVE", "CAPITAL RESERVE"],
];

export function box10Lines(record: Record1098): string[] {
  const lines: string[] = [];
  if (hasValue(record.BOX10_OTHER)) lines.push(text(record.BOX10_OTHER));
  for (const [column, label] of BOX10_AMOUNT_LINES) {
    if (hasValue(record[column])) {
      lines.push(`${label} $ ${parseAmount(record[column])}`);
    }
  }
  return lines;
}

/** IRS OtherTxt is at most 150 characters. */
export const OTHER_TXT_MAX = 150;

export function box10OtherTxt(record: Record1098): string {
  return box10Lines(record).join("; ").slice(0, OTHER_TXT_MAX).trim();
}

export function propertyDescription(record: Record1098): string {
  return [
    record.BOX8_ADDRESS_OR_DESCRIPTION_OF_PROPERTY_LINE_1,
    record.BOX8_ADDRESS_OR_DESCRIPTION_OF_PROPERTY_LINE_2,
    record.BOX8_ADDRESS_OR_DESCRIPTION_OF_PROPERTY_LINE_3,
  ]
    .map(text)
    .filter(Boolean)
    .join(" ");
}

const amountIfPresent = (element: string, value: unknown) =>
  hasValue(value) ? { [element]: parseAmount(value) } : {};

/** The boxes after RecipientAccountNum, in schema order. */
export function form1098Boxes(record: Record1098): Record<string, any> {
  const origination = irsDate(record.BOX3_MORTGAGE_ORIGNATION_DATE);
  const acquisition = irsDate(record.BOX11_MORTGAGE_ACQUISITION_DATE);
  const description = propertyDescription(record);
  const count = text(record.BOX9_NUMBER_OF_PROPERTIES);
  const other = box10OtherTxt(record);
  return {
    ...amountIfPresent("MortgageInterestReceivedAmt", record.BOX1_MORTGAGE_INTEREST),
    ...amountIfPresent("OutstandingMortgPrincipalAmt", record.BOX2_OUTSTANDING_MORTGAGE_PRINCIPAL),
    ...(origination && { MortgageOriginationDt: origination }),
    ...amountIfPresent("OverpaidInterestRefundAmt", record.BOX4_REFUND_OF_OVERPAID_INTEREST),
    ...amountIfPresent("MortgageInsurancePremiumsAmt", record.BOX_5_MORTGAGE_INSURANCE_PREMIUM),
    ...amountIfPresent("PrinResPurchasePointsPaidAmt", record.BOX6_POINTS_PAID),
    // Required by the schema: 1 when the property is at the borrower's address.
    PropAddrSameBorrowerAddrInd: isChecked(
      record.BOX7_ADDRESS_OF_PROPERTY_SECURING_MORTGAGE_IS_THE_SAME,
    )
      ? "1"
      : "0",
    ...(description && { PropertyDesc: description }),
    // The IRS count is an integer; a decimal box 9 prints on the form but is not filed.
    ...(/^\d+$/.test(count) && { PropertiesSecuringMortgageCnt: String(parseInt(count, 10)) }),
    ...(other && { OtherTxt: other }),
    ...(acquisition && { MortgageAcquisitionDt: acquisition }),
  };
}

/** RecipientDetail for the borrower (person or business, US or foreign). */
export function borrowerRecipientDetail(
  record: Record1098,
  testFile: boolean,
  schemaRoot?: string,
): object {
  const tin = testFile ? "000000000" : borrowerTin(record);
  const mailing = borrowerMailingAddress(record, schemaRoot);
  const recipient = borrowerAsRecipient(record);
  return (
    buildSplitRecipientDetail(recipient, tin, mailing) ?? {
      // A row with no name at all: upload validation refuses these, so this
      // only guards a record edited around it.
      TIN: tin,
      TINSubmittedTypeCd: "INDIVIDUAL_TIN",
      PersonNameControlTxt: "UNKN",
      PersonName: { PersonFirstNm: "UNKNOWN", PersonLastNm: "UNKNOWN" },
      MailingAddressGrp: mailing,
    }
  );
}

/** Issuer-level totals: the five amount boxes Form1098TotalAmtGrpType carries. */
export function form1098Totals(records: Record1098[]): Record<string, string> {
  const sum = (column: keyof Record1098) =>
    records.reduce((total, r) => total + parseFloat(parseAmount(r[column] ?? "0")), 0).toFixed(2);
  return {
    MortgageInterestReceivedAmt: sum("BOX1_MORTGAGE_INTEREST"),
    OutstandingMortgPrincipalAmt: sum("BOX2_OUTSTANDING_MORTGAGE_PRINCIPAL"),
    OverpaidInterestRefundAmt: sum("BOX4_REFUND_OF_OVERPAID_INTEREST"),
    MortgageInsurancePremiumsAmt: sum("BOX_5_MORTGAGE_INSURANCE_PREMIUM"),
    PrinResPurchasePointsPaidAmt: sum("BOX6_POINTS_PAID"),
  };
}
